import {
  afterRenderEffect,
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  inject,
  input,
  signal,
  viewChild,
} from '@angular/core';
import { ICaseBattle } from '../interfaces/case-battles.interface';
import { SvgIconComponent } from 'angular-svg-icon';
import { ICaseWithQuantity } from '@features/cases/interfaces/cases.interface';
import { ModalService } from '@features/modals/services/modal.service';
import { CasesService } from '@features/cases/services/cases.service';
import {
  NgpTooltip,
  NgpTooltipTrigger,
  provideTooltipConfig,
} from 'ng-primitives/tooltip';
import { SessionService } from '@core/services/session.service';
import { BalanceType } from '@core/types/balance.type';
import { CostComponent } from '../../../core/components/cost/cost.component';
import {
  fadeInAnimation,
  fadeInOutAnimation,
} from '@core/utils/animations.util';

interface CaseProgressSettings {
  perView: number;
}

const DEFAULT_SETTINGS: CaseProgressSettings = {
  perView: 6,
};

@Component({
  selector: 'app-cb-cases-progress',
  imports: [SvgIconComponent, NgpTooltip, NgpTooltipTrigger, CostComponent],
  templateUrl: './cb-cases-progress.component.html',
  styleUrls: ['./cb-cases-progress.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  animations: [fadeInAnimation, fadeInOutAnimation],
  providers: [
    provideTooltipConfig({
      offset: 12,
      placement: 'top',
      showDelay: 0,
      hideDelay: 0,
      flip: true,
      container: document.body,
    }),
  ],
  host: {
    '[style.--cases-per-view]': 'settings().perView',
  },
})
export class CbCasesProgressComponent {
  battle = input.required<ICaseBattle>();
  balance = input.required<BalanceType>();
  settings = input<CaseProgressSettings>(DEFAULT_SETTINGS);

  casesWrapperRef =
    viewChild.required<ElementRef<HTMLElement>>('casesWrapperRef');

  currentCaseIdx = signal<number>(0);
  activeCaseIdx = signal<number>(0);

  private modalService = inject(ModalService);
  private casesService = inject(CasesService);
  public sessionService = inject(SessionService);

  cases = computed<ICaseWithQuantity[]>(() => {
    const battle = this.battle();
    const cases: ICaseWithQuantity[] = battle.cases
      .map((caseItem) => {
        const currCase = this.casesService.findCaseByLink(caseItem.link);
        if (!currCase) return null;

        return {
          ...currCase,
          quantity: 0,
        };
      })
      .filter((caseItem) => caseItem !== null);

    return battle.status === 'finished'
      ? this.getCalculatedCases(cases)
      : cases;
  });

  private autoCenterOnRunning = afterRenderEffect(() => {
    const battle = this.battle();
    const wrapper = this.casesWrapperRef().nativeElement;

    if (battle.status === 'running') {
      const perView = this.settings().perView;
      const itemWidth =
        wrapper.children[0]?.getBoundingClientRect()?.width ?? 56;
      const centerOffset = (perView * itemWidth - itemWidth) / 2; // center first item

      const targetIndex = (battle.rounds?.length ?? 1) - 1;

      const translateX = centerOffset - targetIndex * itemWidth;
      wrapper.style.transition = 'transform 0.4s ease-in-out';
      wrapper.style.transform = `translateX(${translateX}px)`;
      wrapper.style.transition = 'transform 0.3s ease-out';

      // Keep navigation state coherent: approximate left-most index in view
      const leftMostIndex = Math.max(0, targetIndex - Math.floor(perView / 2));
      this.currentCaseIdx.set(leftMostIndex);

      // Track the centered (active) case for dimming others
      this.activeCaseIdx.set(targetIndex);
    }

    if (battle.status === 'finished') {
      wrapper.style.transition = 'transform 0.5s ease-in';
      wrapper.style.transform = 'translateX(0px)';
      wrapper.style.transition = 'transform 0.2s ease-out';
      this.currentCaseIdx.set(0);
      this.activeCaseIdx.set(0);
    }
  });

  public handleOpenSkinsPopup(caseItem: ICaseWithQuantity) {
    this.modalService.openModal('CaseBattlesCaseSkinsModalComponent', {
      caseItem: caseItem,
      balance: this.balance(),
    });
  }

  private getCalculatedCases(cases: ICaseWithQuantity[]): ICaseWithQuantity[] {
    const casesMap = new Map();

    cases.forEach((caseItem) => {
      const key = caseItem.link;
      if (casesMap.has(key)) {
        casesMap.get(key).quantity += 1;
      } else {
        casesMap.set(key, { ...caseItem, quantity: 1 });
      }
    });

    return Array.from(casesMap.values());
  }

  public moveSlide(direction: 'left' | 'right') {
    const wrapper = this.casesWrapperRef()?.nativeElement;
    const prevTransformX = Number(
      wrapper.style.transform.split('(')[1]?.split('px')[0] || '0'
    );
    wrapper.style.transform = `translateX(${
      direction === 'left' ? prevTransformX + 56 : prevTransformX - 56
    }px)`;
    this.currentCaseIdx.update((prev) => {
      if (direction === 'left') {
        return prev - 1;
      } else {
        return prev + 1;
      }
    });
  }
}
